import React, { FC } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { styled } from "@mui/material/styles";
import { selectedVariations } from "@src/store/slices/variations";

import { useGraphValueFormatter } from "../../../api/formatters/dashboard";
import theme from "../../../theme";
import { StackedBarMetricData } from "../../../types/analytics";

import { GraphConfig, GraphType } from "./Utils/graphConfigs";
import {
  getBarSize,
  getStackedBarRadius,
  getStackedBarStackId,
  getStackedDataMax,
  groupColors,
  stackedBarGroupColors,
  stackedDataMaxFormatter,
  valueAccessor,
} from "./Utils/graphHelpers";
import {
  CustomizedAxisTick,
  CustomLabel,
  CustomResponsiveContainer,
  renderStackedBarLabelList,
  StyledLegendText,
} from "./Utils/graphStyleCustoms";

interface StackedBarChartProps {
  config: GraphConfig;
  groupMetricData: StackedBarMetricData[];
}

const StyledStackedBarChart = styled(BarChart)(({ theme }) => ({
  [`& .recharts-cartesian-axis-tick-value`]: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
  },
}));

export const StackedBarGraph: FC<StackedBarChartProps> = ({
  config,
  groupMetricData,
}) => {
  const variations = useSelector(selectedVariations);
  const updatedConfig = {
    ...config,
    stackedBarLegendTitles:
      config.stackedBarLegendTitles || groupMetricData[0].allIds!,
  };

  const graphValueFormatter = useGraphValueFormatter();

  const dataMax = stackedDataMaxFormatter(
    updatedConfig,
    getStackedDataMax(groupMetricData, updatedConfig)
  );

  return (
    <CustomResponsiveContainer width="100%" height={300}>
      <StyledStackedBarChart
        style={{ marginLeft: -14 }}
        margin={{
          top: 5,
          bottom: 5,
        }}
        data={groupMetricData}
      >
        <CartesianGrid
          style={{ stroke: theme.palette.disabled.light }}
          vertical={false}
        />
        <XAxis
          interval={0}
          tick={(props: any) => {
            return (
              // @ts-ignore
              <CustomizedAxisTick
                props={{
                  ...props,
                  payload: {
                    ...props.payload,
                    value:
                      variations.find((v) => v.id === props.payload.value)
                        ?.name || "",
                  },
                }}
                groupMetricData={groupMetricData}
                graphValueFormatter={graphValueFormatter}
                config={updatedConfig}
              />
            );
          }}
          axisLine={{ stroke: theme.palette.disabled.light }}
          tickLine={false}
          dataKey="id"
        />
        <YAxis
          domain={[!config.autoYAxisMin ? "dataMin" : "auto", dataMax]}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: any) =>
            graphValueFormatter(true, value, updatedConfig)!
          }
        />
        <Tooltip
          contentStyle={{
            fontFamily: theme.typography.fontFamily,
            borderRadius: 5,
            fontSize: 14,
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            color: "#FFF",
            border: "1px solid transparent",
          }}
          itemStyle={{ color: "#FFF" }}
          cursor={{ fill: "transparent" }}
          wrapperStyle={{ zIndex: 34435 }}
          formatter={(value: any) =>
            graphValueFormatter(false, value, updatedConfig)!
          }
        />
        {updatedConfig?.stackedBarLegendTitles?.map((val, index) => (
          <Bar
            key={val}
            isAnimationActive={false}
            name={val}
            dataKey={val}
            stackId={getStackedBarStackId(index, updatedConfig)}
            fill={
              updatedConfig.title[0] === "Units by Product Group" ||
              updatedConfig.title[0] === "Revenue by Product Group"
                ? stackedBarGroupColors[index]
                : groupColors[index]
            }
            radius={
              updatedConfig.title[0] === "Units by Product Group" ||
              updatedConfig.title[0] === "Revenue by Product Group"
                ? [0, 0, 0, 0]
                : getStackedBarRadius(index, updatedConfig)
            }
            barSize={getBarSize(false, groupMetricData.length)}
          >
            {config.graphType === GraphType.ClusteredBar ? (
              <LabelList
                content={(props: any) => {
                  return (
                    // @ts-ignore
                    <CustomLabel
                      props={props}
                      config={config}
                      graphValueFormatter={graphValueFormatter}
                      compareGroupA={undefined}
                      compareGroupB={undefined}
                    />
                  );
                }}
              />
            ) : (
              renderStackedBarLabelList(
                index,
                valueAccessor,
                updatedConfig,
                graphValueFormatter
              )
            )}
          </Bar>
        ))}

        <Legend
          wrapperStyle={{
            paddingLeft: "25px",
            paddingTop: "10px",
          }}
          align={"center"}
          formatter={(value, entry, index) => {
            return StyledLegendText(value, entry, index);
          }}
          iconType={"circle"}
          iconSize={9}
        />
      </StyledStackedBarChart>
    </CustomResponsiveContainer>
  );
};
